import React from 'react';
import _ from 'lodash';
import Mouse from 'core/mouse';

// Components
import TextField from '../text-field';
import { FormField } from '../form-field';

import { round, clamp } from 'helpers/math';
// import { NumberFieldProps } from '../fields.d.ts';
import { REQUIRED } from '../constants';
import { isValue } from '../utils';

interface NumberFieldState {
  tmpValue: number;
  formatOpen: false;
}
export default class NumberField extends FormField<NumberFieldProps, NumberFieldState> {
  static get defaultValue() {
    return 0;
  }

  static getEvaledValue(value) {
    value = value || '';

    if (value.length) {
      value = value.replace(/,/g, '.').replace(/(?![0-9+\*\/\-\.])./g, '');
      value = value === '0' ? value : value.replace(/^0*/, '');

      try {
        return eval(value); // eslint-disable-line no-eval
      } catch (e) {
        return 0;
      }
    }

    return undefined;
  }

  static getErrors(props: NumberFieldProps, formData): string | false {
    const value = isValue(props.value) ? props.value : (formData || {})[props.name];

    let error = false;
    if (props.required && !isValue(value)) error = REQUIRED;
    else if (this.textField) error = TextField.getErrors(this.textField.props, formData);

    return error;
  }

  state = {
    id: _.uniqueId('stepper'),
    focus: false,
    tmpValue: 0
  };

  componentWillUnmount() {
    // Removing events in case component was removed in the middle of action
    this.removeEvents();
    Mouse.off('mousedown', this.handleFormatClose);
  }

  setValue(value) {
    const { onChange } = this.props;
    value = this.getRoundedValue(value);

    if (this.props.value !== value) {
      this.setState({ errors: false });
      if (onChange) onChange(value);
    }
  }

  getRoundedValue(value) {
    return clamp(round(value, this.getPrecision()), this.getMin(), this.getMax()) / this.getScale();
  }

  getDisplayValue(value = this.props.value) {
    if (value === undefined || value === null || isNaN(value) || value === false) return false;
    return round((value || 0) * this.getScale(), this.getPrecision());
  }

  getParsedInput(): number {
    const { tmpValue = NumberField.defaultValue } = this.state;

    if (typeof tmpValue === 'boolean') return 0;
    return _.isNumber(tmpValue) ? tmpValue : parseFloat(tmpValue.replace(',', '.')) || 0;
  }

  getStep() {
    return this.props.step || 1;
  }

  getMin() {
    return typeof this.props.min === 'undefined' ? -Infinity : this.props.min;
  }

  getMax() {
    let max = this.props.max;
    return typeof max === 'undefined' ? Infinity : max;
  }

  getPrecision() {
    return this.props.precision || 3;
  }

  getScale() {
    return this.props.scale || 1;
  }

  getEventDistance() {
    const dx = Mouse.pageX - this.initX;
    const dy = Mouse.pageY - this.initY;
    return Math.abs(dx) >= Math.abs(dy) ? dx : dy;
  }
  removeEvents() {
    Mouse.off('mousemove', this.handleMouseMove);
    Mouse.off('mouseup', this.handleMouseUp);
    document.body.classList.remove('dragging');
  }

  validate() {
    const errors = NumberField.getErrors(this.props);
    this.setState({ errors });

    return !errors;
  }

  handleBlur = e => {
    if (!this.props.noValidation) this.setState({ focus: false }, this.validate);
    else this.setState({ focus: false });

    if (this.props.onBlur) this.props.onBlur(e);
    if (this.state.changed) {
      this.setValue(NumberField.getEvaledValue(this.state.tmpValue));
    }
  };

  handleChange = input => {
    this.setState({ tmpValue: input, changed: true });
    if (input || input === 0) {
      this.setValue(NumberField.getEvaledValue(input));
    }
  };

  handleFocus = () => {
    this.setState({ focus: true, tmpValue: this.getDisplayValue(), changed: false });

    if (this.props.onFocus) this.props.onFocus();
  };

  handleKeyDown = e => {
    if (e.which === 27 || e.which === 13) this.handleBlur(e);
    // 38 is up key and 40 is down key, so 39 - (up || down) is either 1 or -1,
    const stepDir = 39 - e.which;
    // this can be used to find out the step direction and if a step should be done
    if (Math.abs(stepDir) === 1) {
      // Prevent default so the cursor doesn't move
      e.preventDefault();
      this.handleArrowStep(stepDir, e.shiftKey, e.altKey);
    }
  };

  handleMouseDown = e => {
    if (this.state.focus) return;
    e.preventDefault();
    this.hasMoved = false;
    this.initX = Mouse.pageX;
    this.initY = Mouse.pageY;
    this.initValue = this.getDisplayValue();
    document.body.classList.add('dragging');
    Mouse.on('mousemove', this.handleMouseMove);
    Mouse.on('mouseup', this.handleMouseUp);
  };

  handleMouseMove = e => {
    let dist = this.getEventDistance(e);
    if (this.hasMoved || Math.abs(dist) > 5) {
      this.hasMoved = true;
      this.handleStep(dist, e.shiftKey, e.altKey);
    }
  };

  handleMouseUp = () => {
    this.removeEvents();

    if (!this.hasMoved) {
      this.handleFocus();
    }
  };

  handleScroll = e => {
    const { focus } = this.state;
    if (focus) {
      this.handleArrowStep(e.deltaY < 0 ? -1 : 1, e.shiftKey, e.altKey);
      e.preventDefault();
    }
  };

  handleStep = (dir, big, small) => {
    // dir = Math.min(Math.max(-1, dir), 1);
    let dist = this.getStep() * dir * (big ? 10 : 1) * (small ? 0.1 : 1);
    if (dist !== 0) {
      const value = this.initValue + dist;
      this.setState({ tmpValue: this.initValue / this.getScale() });
      console.log('HANDLE STEP', value);
      this.setValue(value);
    }
  };

  handleArrowStep = (dir, big, small) => {
    dir = Math.min(Math.max(-1, dir), 1);
    let dist = this.getStep() * dir * (big ? 10 : 1) * (small ? 0.1 : 1);
    if (dist !== 0) {
      const value = this.getDisplayValue() + dist;
      this.setState({ tmpValue: this.getDisplayValue(value) / this.getScale() });
      this.setValue(value);
    }
  };

  render() {
    // eslint-disable-next-line no-unused-vars
    const {
      disabled,
      inputClassName,
      inputStyle,
      label,
      max,
      min,
      tabIndex,
      container,
      ...rest
    } = this.props;
    const { tmpValue, focus, id, errors } = this.state;

    const displayValue = focus ? tmpValue : this.getDisplayValue();

    return (
      <TextField
        {...rest}
        name={id}
        ref={ref => (this.textField = ref)}
        error={errors}
        disabled={disabled}
        styles={inputStyle}
        placeholder={'-'}
        value={displayValue}
        tabIndex={tabIndex}
        label={label}
        onMouseDown={this.handleMouseDown}
        onKeyDown={this.handleKeyDown}
        onFocus={this.handleFocus}
        onBlur={this.handleBlur}
        onChange={this.handleChange}
        onWheel={this.handleScroll}
        type={'text'}
      />
    );
  }
}
